import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDate as formatCurrentDate } from '@/utils/dateFormat';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Clock, 
  MapPin, 
  Coffee, 
  LogOut, 
  Bell,
  CheckCircle,
  XCircle,
  UtensilsCrossed,
  Building2,
  History,
  CalendarDays,
  LayoutDashboard,
  Camera,
  AlertTriangle,
  User,
  FolderKanban,
  WifiOff,
  CloudOff,
  RefreshCw,
  Timer
} from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import NotificationPanel from '@/components/NotificationPanel';
import FaceEnrollment from '@/components/FaceEnrollment';
import FaceVerification from '@/components/FaceVerification';
import OvertimeProgressCard from '@/components/OvertimeProgressCard';
import PushNotificationManager from '@/components/PushNotificationManager';
import { 
  initOfflineDB, 
  createOfflineClockIn, 
  createOfflineClockOut, 
  createOfflineBreakStart, 
  createOfflineBreakEnd,
  getPendingCount,
  isOnline as checkIsOnline
} from '@/utils/offlineStorage';
import { syncAllPendingActions, setupAutoSync, syncOnStartup } from '@/utils/offlineSync';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EmployeeDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  
  // Debug: Log user's manager status
  useEffect(() => {
    if (user) {
      console.log('User manager check:', { 
        is_manager: user.is_manager, 
        role: user.role,
        should_show_portal: (user.is_manager || user.role === 'manager') && user.role !== 'company_admin'
      });
    }
  }, [user]);
  
  const [currentTime, setCurrentTime] = useState(new Date());
  const [location, setLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const [geofenceStatus, setGeofenceStatus] = useState({ inside: false, office_name: null });
  const [geofenceChecked, setGeofenceChecked] = useState(false);
  const [timeEntry, setTimeEntry] = useState(null);
  const [currentBreak, setCurrentBreak] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [breakDuration, setBreakDuration] = useState(0);
  const [breakAlertShown, setBreakAlertShown] = useState(false);
  
  // Facial Recognition states
  const [facialSettings, setFacialSettings] = useState(null);
  const [faceEnrolled, setFaceEnrolled] = useState(false);
  const [showEnrollmentDialog, setShowEnrollmentDialog] = useState(false);
  const [showVerificationDialog, setShowVerificationDialog] = useState(false);
  const [pendingClockIn, setPendingClockIn] = useState(false);
  
  // Period Progress visibility
  const [showPeriodProgress, setShowPeriodProgress] = useState(true);
  
  // Project tracking
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState('');

  // Offline state tracking
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [pendingActionsCount, setPendingActionsCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  // Initialize offline database and sync on startup
  useEffect(() => {
    const initOffline = async () => {
      try {
        await initOfflineDB();
        const pending = await getPendingCount();
        setPendingActionsCount(pending);
        
        // Sync any pending actions if online
        if (navigator.onLine && pending > 0) {
          setIsSyncing(true);
          const result = await syncOnStartup();
          if (result.synced > 0) {
            toast.success(`Synced ${result.synced} offline action(s)`);
            fetchStatus();
          }
          setPendingActionsCount(result.remaining);
          setIsSyncing(false);
        }
      } catch (e) {
        console.error('Failed to init offline DB:', e);
      }
    };
    
    initOffline();
    
    // Set up auto-sync when coming back online
    const cleanup = setupAutoSync(async (result) => {
      if (result.synced > 0) {
        toast.success(`Synced ${result.synced} offline action(s)`);
        fetchStatus();
      }
      setPendingActionsCount(result.remaining);
    });
    
    // Track online/offline status
    const handleOnline = () => {
      setIsOffline(false);
      toast.success('Back online!');
    };
    const handleOffline = () => {
      setIsOffline(true);
      toast.warning('You are offline. Actions will be saved locally.');
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      cleanup();
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Update pending count periodically
  useEffect(() => {
    const checkPending = async () => {
      const count = await getPendingCount();
      setPendingActionsCount(count);
    };
    const interval = setInterval(checkPending, 10000);
    return () => clearInterval(interval);
  }, []);

  // Update current time every second
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Get user location with retry logic
  const [locationRetries, setLocationRetries] = useState(0);
  const [locationInitialized, setLocationInitialized] = useState(false);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);
  const maxRetries = 3;
  
  // Request location - only runs once on mount, or on explicit retry
  useEffect(() => {
    // Skip if already have a valid location and not explicitly retrying
    if (location && locationRetries === 0) {
      setLocationInitialized(true);
      return;
    }
    
    // Skip if already initialized and not retrying
    if (locationInitialized && locationRetries === 0) {
      return;
    }
    
    // Skip if already requesting
    if (isRequestingLocation) {
      return;
    }
    
    if (!navigator.geolocation) {
      setLocationError('Geolocation is not supported by your browser.');
      setLocationInitialized(true);
      return;
    }
    
    setIsRequestingLocation(true);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const accuracy = position.coords.accuracy;
        console.log(`Location obtained: ${position.coords.latitude}, ${position.coords.longitude} (accuracy: ${accuracy}m)`);
        
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: accuracy
        });
        setLocationError(null);
        setLocationRetries(0);
        setLocationInitialized(true);
        setIsRequestingLocation(false);
      },
      (error) => {
        console.error('Geolocation error:', error);
        
        // Only set error if we don't already have a valid location
        // This prevents flickering when location was already obtained
        if (!location) {
          let errorMsg = 'Unable to get your location.';
          
          switch(error.code) {
            case error.PERMISSION_DENIED:
              errorMsg = 'Location access denied. Please enable location permissions in your browser settings.';
              break;
            case error.POSITION_UNAVAILABLE:
              errorMsg = 'Location information unavailable. Please check your device settings.';
              break;
            case error.TIMEOUT:
              errorMsg = 'Location request timed out. Please try again.';
              break;
            default:
              errorMsg = 'Unable to get your location. Please enable location services.';
          }
          
          setLocationError(errorMsg);
        }
        
        setLocationInitialized(true);
        setIsRequestingLocation(false);
        
        // Auto-retry up to maxRetries times with increasing delay (only if no location yet)
        if (!location && locationRetries < maxRetries) {
          const delay = (locationRetries + 1) * 2000; // 2s, 4s, 6s
          setTimeout(() => {
            setLocationRetries(prev => prev + 1);
          }, delay);
        }
      },
      { 
        enableHighAccuracy: true,
        timeout: 20000,
        maximumAge: 0 // Always get fresh location, no cache
      }
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationRetries]); // Only re-run on retry, not on location change

  // Check if inside geofence
  const checkGeofence = useCallback(async () => {
    if (!location) return;
    try {
      const response = await axios.post(`${API}/geofence/check`, location);
      setGeofenceStatus(response.data);
      setGeofenceChecked(true);
      console.log('Geofence check result:', response.data);
    } catch (error) {
      console.error('Geofence check error:', error);
      setGeofenceChecked(true);
    }
  }, [location]);

  useEffect(() => {
    if (location) {
      checkGeofence();
      // Recheck every 10 seconds in case user moves
      const interval = setInterval(checkGeofence, 10000);
      return () => clearInterval(interval);
    }
  }, [location, checkGeofence]);

  // Refresh location on demand
  const [isRefreshingLocation, setIsRefreshingLocation] = useState(false);
  
  const refreshLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }
    
    setIsRefreshingLocation(true);
    setLocationError(null);
    
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const accuracy = position.coords.accuracy;
        console.log(`Location refreshed: ${position.coords.latitude}, ${position.coords.longitude} (accuracy: ${accuracy}m)`);
        
        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: accuracy
        });
        setLocationError(null);
        setLocationRetries(0);
        
        if (accuracy > 100) {
          toast.warning(`Location updated (accuracy: ~${Math.round(accuracy)}m). Move to an open area for better accuracy.`);
        } else {
          toast.success(`Location updated (accuracy: ~${Math.round(accuracy)}m)`);
        }
        setIsRefreshingLocation(false);
      },
      (error) => {
        console.error('Geolocation refresh error:', error);
        let errorMsg = 'Could not refresh location.';
        
        switch(error.code) {
          case error.PERMISSION_DENIED:
            errorMsg = 'Location access denied. Check browser permissions.';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMsg = 'Location unavailable. Check device settings.';
            break;
          case error.TIMEOUT:
            errorMsg = 'Location request timed out.';
            break;
          default:
            errorMsg = 'Could not refresh location.';
        }
        
        toast.error(errorMsg);
        setLocationError(errorMsg);
        setIsRefreshingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  };

  const [breakSettings, setBreakSettings] = useState({
    breakfast_duration_minutes: 30,
    lunch_duration_minutes: 60,
    general_break_duration_minutes: 15,
    break_warning_enabled: true,
    break_warning_threshold_minutes: 60
  });

  // Fetch current status
  const fetchStatus = useCallback(async () => {
    try {
      const [entryRes, breakRes, notifRes, facialRes, enrollmentRes, visibilityRes, projectsRes] = await Promise.all([
        axios.get(`${API}/time-entries/current`),
        axios.get(`${API}/breaks/current`),
        axios.get(`${API}/notifications/unread-count`),
        axios.get(`${API}/settings/facial-recognition`).catch(() => ({ data: null })),
        axios.get(`${API}/face/enrollment-status`).catch(() => ({ data: { has_face_enrolled: false } })),
        axios.get(`${API}/overtime/visibility-settings`).catch(() => ({ data: { show_period_progress_to_employees: true } })),
        axios.get(`${API}/projects`).catch(() => ({ data: [] }))
      ]);
      setTimeEntry(entryRes.data);
      setCurrentBreak(breakRes.data);
      setUnreadCount(notifRes.data.count);
      // Fetch break settings separately if needed
      try {
        const breakSettingsRes = await axios.get(`${API}/settings/breaks`);
        if (breakSettingsRes.data) {
          setBreakSettings(breakSettingsRes.data);
        }
      } catch (e) {
        console.log('Could not fetch break settings');
      }
      if (facialRes.data) {
        setFacialSettings(facialRes.data);
      }
      setFaceEnrolled(enrollmentRes.data?.has_face_enrolled || false);
      setShowPeriodProgress(visibilityRes.data?.show_period_progress_to_employees !== false);
      setProjects(projectsRes.data || []);
    } catch (error) {
      console.error('Status fetch error:', error);
      // If we get a 401 error, the axios interceptor will handle logout
      // For other errors, reset the state to avoid stale data
      if (error.response?.status !== 401) {
        // Only reset if it's a different kind of error (like network issue)
        // This prevents the UI from getting stuck
        if (!navigator.onLine) {
          console.log('Network offline - keeping current state');
        }
      }
    }
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 30000);
    return () => clearInterval(interval);
  }, [fetchStatus]);

  // Update break duration
  useEffect(() => {
    if (currentBreak) {
      const updateDuration = () => {
        const start = new Date(currentBreak.start_time);
        const now = new Date();
        const minutes = (now - start) / 60000;
        setBreakDuration(minutes);
      };
      updateDuration();
      const timer = setInterval(updateDuration, 1000);
      return () => clearInterval(timer);
    } else {
      setBreakDuration(0);
    }
  }, [currentBreak]);

  // Check break duration for notification using company settings
  useEffect(() => {
    const threshold = breakSettings.break_warning_threshold_minutes || 60;
    if (breakSettings.break_warning_enabled && breakDuration >= threshold && currentBreak && !breakAlertShown) {
      // Show toast notification
      toast.warning(`Your ${currentBreak.break_type} break has exceeded ${threshold} minutes!`, {
        duration: 10000,
        description: 'Please end your break or notify your manager.',
      });
      setBreakAlertShown(true);
      // Also call API to create notification in DB
      axios.post(`${API}/breaks/check-duration`).catch(console.error);
    }
  }, [breakDuration, currentBreak, breakAlertShown, breakSettings]);

  // Reset break alert when break ends
  useEffect(() => {
    if (!currentBreak) {
      setBreakAlertShown(false);
    }
  }, [currentBreak]);

  // Initiate clock-in (may require face verification)
  const initiateClockIn = async () => {
    // Admins can bypass facial recognition requirements
    const isAdmin = user?.role === 'company_admin';
    
    // Check if facial recognition is enabled (skip for admins)
    if (facialSettings?.enabled && !isAdmin) {
      // Check if enrollment is required but not done
      if (facialSettings.enrollment_required && !faceEnrolled) {
        toast.error('You must enroll your face before clocking in. Click "Enroll Face" below.');
        return;
      }
      
      // If enrolled, show verification dialog
      if (faceEnrolled) {
        setPendingClockIn(true);
        setShowVerificationDialog(true);
        return;
      }
    }
    
    // No facial recognition, admin bypass, or not enrolled - proceed with regular clock-in
    handleClockIn();
  };

  // Handle face verification result
  const onFaceVerificationComplete = (verified, confidence) => {
    setShowVerificationDialog(false);
    
    if (verified) {
      // Face verified - proceed with clock-in
      handleClockIn();
    } else {
      // Face not verified
      if (facialSettings?.enforcement_mode === 'block') {
        toast.error('Face verification failed. Clock-in blocked.');
        setPendingClockIn(false);
      } else {
        // Warn mode - allow clock-in but log the warning
        toast.warning('Face verification failed, but proceeding with clock-in (warn mode).');
        handleClockIn();
      }
    }
  };

  const handleClockIn = async () => {
    // For offline mode, we can proceed without location but will record it if available
    // For online mode, location is required
    if (!location && !isOffline && navigator.onLine) {
      toast.error('Location not available. Please enable location services.');
      return;
    }
    
    // Online mode: Check geofence (unless off-site permission)
    if (!isOffline && navigator.onLine && !geofenceStatus.inside_geofence && !user?.allow_offsite_clock) {
      toast.error('You must be within the office geofence to clock in.');
      return;
    }
    
    setIsLoading(true);
    
    // If offline, save locally
    if (isOffline || !navigator.onLine) {
      try {
        // Use location if available, otherwise use placeholder
        const offlineLocation = location || { latitude: 0, longitude: 0, accuracy: null };
        await createOfflineClockIn(offlineLocation, (selectedProject && selectedProject !== 'none') ? selectedProject : null);
        const count = await getPendingCount();
        setPendingActionsCount(count);
        
        // Create a mock time entry for UI
        setTimeEntry({
          id: 'offline-' + Date.now(),
          clock_in: new Date().toISOString(),
          office_name: 'Offline',
          is_offline: true
        });
        
        const locationMsg = location ? 'Clock-in saved offline!' : 'Clock-in saved offline (no GPS)!';
        toast.success(locationMsg + ' Will sync when back online.', {
          icon: <WifiOff className="w-4 h-4" />,
          duration: 5000
        });
        setPendingClockIn(false);
        setSelectedProject('');
      } catch (error) {
        toast.error('Failed to save offline clock-in: ' + (error.message || 'Unknown error'));
        console.error('Offline clock-in error:', error);
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    // Online mode: Send to server
    try {
      const payload = {
        ...location,
        project_id: (selectedProject && selectedProject !== 'none') ? selectedProject : null
      };
      const response = await axios.post(`${API}/time-entries/clock-in`, payload);
      setTimeEntry(response.data);
      const locationMsg = response.data.is_offsite ? 'Off-site' : (response.data.office_name || 'office');
      toast.success(`Clocked in at ${locationMsg}!`);
      setPendingClockIn(false);
      setSelectedProject(''); // Reset project selection
      
      // Dismiss late clock-in notifications after successful clock-in
      try {
        await axios.put(`${API}/notifications/dismiss-by-type`, { type: 'late_clockin' });
        fetchUnreadCount(); // Refresh notification count
      } catch (e) {
        // Silently ignore if dismissal fails
      }
    } catch (error) {
      // If network error, try offline save
      if (!error.response) {
        try {
          const offlineLocation = location || { latitude: 0, longitude: 0, accuracy: null };
          await createOfflineClockIn(offlineLocation, (selectedProject && selectedProject !== 'none') ? selectedProject : null);
          const count = await getPendingCount();
          setPendingActionsCount(count);
          setIsOffline(true);
          
          setTimeEntry({
            id: 'offline-' + Date.now(),
            clock_in: new Date().toISOString(),
            office_name: 'Offline',
            is_offline: true
          });
          
          toast.warning('Server unreachable. Clock-in saved offline!');
          setPendingClockIn(false);
          setSelectedProject('');
        } catch (offlineError) {
          toast.error('Failed to save clock-in: ' + (offlineError.message || 'Unknown error'));
        }
      } else {
        toast.error(error.response?.data?.detail || 'Failed to clock in');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClockOut = async () => {
    setIsLoading(true);
    
    // If offline, save locally
    if (isOffline || !navigator.onLine) {
      try {
        await createOfflineClockOut(location);
        const count = await getPendingCount();
        setPendingActionsCount(count);
        
        setTimeEntry(null);
        setCurrentBreak(null);
        
        toast.success('Clock-out saved offline! Will sync when back online.', {
          icon: <WifiOff className="w-4 h-4" />,
          duration: 5000
        });
      } catch (error) {
        toast.error('Failed to save offline clock-out');
        console.error('Offline clock-out error:', error);
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    // Online mode
    try {
      await axios.post(`${API}/time-entries/clock-out`);
      setTimeEntry(null);
      setCurrentBreak(null);
      toast.success('Clocked out successfully!');
    } catch (error) {
      // If network error, try offline save
      if (!error.response) {
        try {
          await createOfflineClockOut(location);
          const count = await getPendingCount();
          setPendingActionsCount(count);
          setIsOffline(true);
          
          setTimeEntry(null);
          setCurrentBreak(null);
          
          toast.warning('Server unreachable. Clock-out saved offline!');
        } catch (offlineError) {
          toast.error('Failed to save clock-out');
        }
      } else {
        toast.error(error.response?.data?.detail || 'Failed to clock out');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleStartBreak = async (breakType) => {
    setIsLoading(true);
    
    // If offline, save locally
    if (isOffline || !navigator.onLine) {
      try {
        await createOfflineBreakStart(breakType);
        const count = await getPendingCount();
        setPendingActionsCount(count);
        
        // Create mock break for UI
        setCurrentBreak({
          id: 'offline-' + Date.now(),
          break_type: breakType,
          start_time: new Date().toISOString(),
          is_offline: true
        });
        
        toast.success(`${breakType.charAt(0).toUpperCase() + breakType.slice(1)} break saved offline!`, {
          icon: <WifiOff className="w-4 h-4" />
        });
      } catch (error) {
        toast.error('Failed to save offline break start');
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    // Online mode
    try {
      const response = await axios.post(`${API}/breaks/start`, { break_type: breakType });
      setCurrentBreak(response.data);
      toast.success(`${breakType.charAt(0).toUpperCase() + breakType.slice(1)} break started!`);
    } catch (error) {
      // If network error, try offline
      if (!error.response) {
        try {
          await createOfflineBreakStart(breakType);
          const count = await getPendingCount();
          setPendingActionsCount(count);
          setIsOffline(true);
          
          setCurrentBreak({
            id: 'offline-' + Date.now(),
            break_type: breakType,
            start_time: new Date().toISOString(),
            is_offline: true
          });
          
          toast.warning('Server unreachable. Break saved offline!');
        } catch (offlineError) {
          toast.error('Failed to save break');
        }
      } else {
        toast.error(error.response?.data?.detail || 'Failed to start break');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleEndBreak = async () => {
    setIsLoading(true);
    
    // If offline, save locally
    if (isOffline || !navigator.onLine) {
      try {
        await createOfflineBreakEnd();
        const count = await getPendingCount();
        setPendingActionsCount(count);
        
        setCurrentBreak(null);
        toast.success('Break end saved offline!', {
          icon: <WifiOff className="w-4 h-4" />
        });
      } catch (error) {
        toast.error('Failed to save offline break end');
      } finally {
        setIsLoading(false);
      }
      return;
    }
    
    // Online mode
    try {
      await axios.post(`${API}/breaks/end`);
      setCurrentBreak(null);
      toast.success('Break ended!');
    } catch (error) {
      // If network error, try offline
      if (!error.response) {
        try {
          await createOfflineBreakEnd();
          const count = await getPendingCount();
          setPendingActionsCount(count);
          setIsOffline(true);
          
          setCurrentBreak(null);
          toast.warning('Server unreachable. Break end saved offline!');
        } catch (offlineError) {
          toast.error('Failed to save break end');
        }
      } else {
        toast.error(error.response?.data?.detail || 'Failed to end break');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit', 
      second: '2-digit',
      hour12: true 
    });
  };

  const formatDuration = (minutes) => {
    const hrs = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    const secs = Math.floor((minutes * 60) % 60);
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const getWorkDuration = () => {
    if (!timeEntry) return '00:00:00';
    const start = new Date(timeEntry.clock_in);
    const minutes = (currentTime - start) / 60000;
    return formatDuration(minutes);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Offline Status Banner */}
      {(isOffline || pendingActionsCount > 0) && (
        <div className={`${isOffline ? 'bg-amber-500' : 'bg-blue-500'} text-white px-4 py-2`} data-testid="offline-banner">
          <div className="max-w-md mx-auto flex items-center justify-between">
            <div className="flex items-center gap-2">
              {isOffline ? (
                <>
                  <WifiOff className="w-4 h-4" />
                  <span className="text-sm font-medium">Offline Mode</span>
                </>
              ) : (
                <>
                  <CloudOff className="w-4 h-4" />
                  <span className="text-sm font-medium">{pendingActionsCount} action(s) pending sync</span>
                </>
              )}
            </div>
            {!isOffline && pendingActionsCount > 0 && (
              <Button
                size="sm"
                variant="ghost"
                className="text-white hover:bg-white/20 h-7 px-2"
                onClick={async () => {
                  setIsSyncing(true);
                  const result = await syncAllPendingActions();
                  if (result.synced > 0) {
                    toast.success(`Synced ${result.synced} action(s)`);
                    fetchStatus();
                  }
                  setPendingActionsCount(result.remaining);
                  setIsSyncing(false);
                }}
                disabled={isSyncing}
                data-testid="sync-now-btn"
              >
                <RefreshCw className={`w-4 h-4 ${isSyncing ? 'animate-spin' : ''}`} />
                <span className="ml-1 text-xs">{isSyncing ? 'Syncing...' : 'Sync Now'}</span>
              </Button>
            )}
          </div>
        </div>
      )}
      
      {/* Header */}
      <header className="glass-header">
        <div className="max-w-md mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[hsl(243,65%,20%)] flex items-center justify-center text-white">
              <Clock className="w-5 h-5" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Welcome back</p>
              <p className="font-semibold font-['Manrope']">{user?.name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Admin Portal Link - Only for company admins */}
            {user?.role === 'company_admin' && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/admin')}
                title="Admin Portal"
                data-testid="admin-portal-btn"
                className="hidden sm:flex items-center gap-1 text-purple-600 border-purple-200"
              >
                <LayoutDashboard className="w-4 h-4" />
                <span>Admin Portal</span>
              </Button>
            )}
            {user?.role === 'company_admin' && (
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate('/admin')}
                title="Admin Portal"
                data-testid="admin-portal-btn-mobile"
                className="sm:hidden"
              >
                <LayoutDashboard className="w-5 h-5" />
              </Button>
            )}
            {/* Manager Portal Link - Only for managers */}
            {(user?.is_manager || user?.role === 'manager') && user?.role !== 'company_admin' && (
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => navigate('/admin')}
                title="Manager Portal"
                data-testid="manager-portal-btn"
                className="hidden sm:flex items-center gap-1 text-indigo-600 border-indigo-200 bg-indigo-50"
              >
                <LayoutDashboard className="w-4 h-4" />
                <span>Manager Portal</span>
              </Button>
            )}
            {(user?.is_manager || user?.role === 'manager') && user?.role !== 'company_admin' && (
              <Button 
                variant="outline" 
                size="icon" 
                onClick={() => navigate('/admin')}
                title="Manager Portal"
                data-testid="manager-portal-btn-mobile"
                className="sm:hidden text-indigo-600 border-indigo-300 bg-indigo-50 hover:bg-indigo-100"
              >
                <LayoutDashboard className="w-5 h-5" />
              </Button>
            )}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/history')}
              title="View History"
              data-testid="history-btn"
            >
              <History className="w-5 h-5" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => navigate('/employee/roster')}
              title="View Roster"
              data-testid="roster-btn"
            >
              <CalendarDays className="w-5 h-5" />
            </Button>
            <PushNotificationManager />
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              onClick={() => setShowNotifications(true)}
              data-testid="notification-btn"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="notification-badge">{unreadCount}</span>
              )}
            </Button>
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate('/profile')}
              title="My Profile"
              data-testid="profile-btn"
            >
              <User className="w-5 h-5" />
            </Button>
            <Button 
              variant="ghost" 
              className="text-red-600 hover:text-red-700 hover:bg-red-50 gap-2"
              onClick={logout}
              data-testid="employee-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              <span className="hidden sm:inline">Log Out</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-md mx-auto px-4 py-8 space-y-6">
        {/* Company Badge */}
        {user?.company_name && (
          <div className="flex justify-center">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-white rounded-full shadow-sm border text-sm text-slate-600">
              <Building2 className="w-4 h-4" />
              {user.company_name}
            </div>
          </div>
        )}

        {/* Current Time */}
        <Card className="text-center border-0 shadow-lg animate-fade-in">
          <CardContent className="pt-8 pb-8">
            <p className="text-sm text-slate-500 uppercase tracking-wider mb-2">Current Time</p>
            <p className="current-time font-mono" data-testid="current-time">{formatTime(currentTime)}</p>
            <p className="text-slate-500 mt-2">{formatCurrentDate(currentTime)}</p>
          </CardContent>
        </Card>

        {/* Location Status */}
        <div className="space-y-2 animate-fade-in" style={{ animationDelay: '0.1s' }}>
          <div className="flex justify-center">
            {locationError ? (
              <div className="geofence-status outside" data-testid="location-status-error">
                <XCircle className="w-4 h-4" />
                <span>Location unavailable</span>
              </div>
            ) : !geofenceChecked ? (
              <div className="geofence-status bg-slate-100 text-slate-600">
                <MapPin className="w-4 h-4 animate-pulse" />
                <span>Checking location...</span>
              </div>
            ) : geofenceStatus.inside_geofence ? (
              <div className="geofence-status inside" data-testid="location-status-inside">
                <CheckCircle className="w-4 h-4" />
                <span>{geofenceStatus.office_name || geofenceStatus.office?.name || 'Inside work zone'}</span>
              </div>
            ) : (
              <div className="geofence-status outside" data-testid="location-status-outside">
                <XCircle className="w-4 h-4" />
                <span>
                  {geofenceStatus.debug?.closest_office 
                    ? `Outside ${geofenceStatus.debug.closest_office}${geofenceStatus.debug.approx_distance_meters ? ` (~${geofenceStatus.debug.approx_distance_meters}m away)` : ''}` 
                    : 'Outside work zone'}
                </span>
              </div>
            )}
          </div>
          
          {/* Location details and refresh */}
          <div className="flex flex-col items-center gap-1">
            {location && (
              <>
                <p className="text-xs text-slate-400">
                  {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                </p>
                {location.accuracy && (
                  <p className={`text-xs ${location.accuracy > 100 ? 'text-amber-500' : 'text-green-500'}`}>
                    Accuracy: ~{Math.round(location.accuracy)}m
                    {location.accuracy > 100 && ' (low - try outdoors)'}
                  </p>
                )}
              </>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs"
              onClick={refreshLocation}
              disabled={isRefreshingLocation}
              data-testid="refresh-location-btn"
            >
              <MapPin className={`w-3 h-3 mr-1 ${isRefreshingLocation ? 'animate-spin' : ''}`} />
              {isRefreshingLocation ? 'Refreshing...' : 'Refresh'}
            </Button>
          </div>
          
          {/* Error help text */}
          {locationError && (
            <div className="text-center space-y-2 max-w-xs mx-auto">
              <p className="text-xs text-slate-500">
                {locationError}
              </p>
              <p className="text-xs text-blue-600">
                Tip: Make sure location services are enabled in your browser and device settings, then click Refresh.
              </p>
            </div>
          )}
          
          {/* Help text when outside geofence */}
          {geofenceChecked && !geofenceStatus.inside_geofence && !locationError && !user?.allow_offsite_clock && (
            <p className="text-xs text-center text-slate-500 max-w-xs mx-auto">
              Move closer to your office location or contact your manager if you believe this is an error.
            </p>
          )}
          
          {/* Off-site info when allowed */}
          {geofenceChecked && !geofenceStatus.inside_geofence && !locationError && user?.allow_offsite_clock && (
            <div className="flex items-center justify-center gap-2 text-xs text-green-600">
              <CheckCircle className="w-4 h-4" />
              <span>Off-site clock-in enabled for your account</span>
            </div>
          )}
          
          {/* Face enrollment prompt - skip for admins */}
          {facialSettings?.enabled && facialSettings?.enrollment_required && !faceEnrolled && !timeEntry && user?.role !== 'company_admin' && (
            <div className="mt-3 p-3 bg-orange-50 border border-orange-200 rounded-lg max-w-xs mx-auto">
              <div className="flex items-center gap-2 text-orange-700">
                <AlertTriangle className="w-5 h-5" />
                <div>
                  <p className="text-sm font-medium">Face Enrollment Required</p>
                  <p className="text-xs">Please enroll your face before clocking in.</p>
                </div>
              </div>
              <Button
                size="sm"
                className="mt-2 w-full bg-orange-600 hover:bg-orange-700 gap-2"
                onClick={() => setShowEnrollmentDialog(true)}
              >
                <Camera className="w-4 h-4" />
                Enroll Face
              </Button>
            </div>
          )}
        </div>

        {/* Project Selection - only show when not clocked in and projects exist */}
        {!timeEntry && projects.length > 0 && (
          <div className="max-w-xs mx-auto mb-4 animate-fade-in" style={{ animationDelay: '0.15s' }}>
            <div className="flex items-center gap-2 mb-2">
              <FolderKanban className="w-4 h-4 text-slate-500" />
              <span className="text-sm text-slate-500">Assign to Project (optional)</span>
            </div>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="w-full" data-testid="project-select">
                <SelectValue placeholder="Select a project..." />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No Project</SelectItem>
                {projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    <div className="flex items-center gap-2">
                      <div 
                        className="w-2 h-2 rounded-full" 
                        style={{ backgroundColor: project.color }}
                      />
                      {project.name}
                      {project.code && <span className="text-xs text-slate-400 ml-1">({project.code})</span>}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Clock In/Out Button */}
        <div className="flex justify-center py-6 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          {!timeEntry ? (
            <button
              className={`big-clock-btn ${(geofenceStatus.inside_geofence || user?.allow_offsite_clock) && !locationError ? 'clock-in' : 'disabled'}`}
              onClick={initiateClockIn}
              disabled={(!geofenceStatus.inside_geofence && !user?.allow_offsite_clock) || !!locationError || isLoading}
              data-testid="clock-in-btn"
            >
              {isLoading ? (
                <div className="spinner" />
              ) : (
                <>
                  <Clock className="w-12 h-12 mb-2" />
                  <span>CLOCK IN</span>
                  {facialSettings?.enabled && faceEnrolled && (
                    <span className="text-xs mt-1 opacity-75 flex items-center justify-center gap-1">
                      <Camera className="w-3 h-3" />
                      Face verification
                    </span>
                  )}
                  {user?.allow_offsite_clock && !geofenceStatus.inside_geofence && !facialSettings?.enabled && (
                    <span className="text-xs mt-1 opacity-75">(Off-site)</span>
                  )}
                </>
              )}
            </button>
          ) : (
            <button
              className="big-clock-btn clock-out"
              onClick={handleClockOut}
              disabled={isLoading}
              data-testid="clock-out-btn"
            >
              {isLoading ? (
                <div className="spinner" />
              ) : (
                <>
                  <Clock className="w-12 h-12 mb-2" />
                  <span>CLOCK OUT</span>
                </>
              )}
            </button>
          )}
        </div>

        {/* Face Enrollment Button - when enabled but not required */}
        {facialSettings?.enabled && !facialSettings?.enrollment_required && !faceEnrolled && !timeEntry && (
          <div className="flex justify-center pb-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowEnrollmentDialog(true)}
              className="gap-2 text-blue-600 border-blue-200"
            >
              <Camera className="w-4 h-4" />
              Enroll Face (Optional)
            </Button>
          </div>
        )}

        {/* Work Duration */}
        {timeEntry && (
          <Card className="border-0 shadow-lg animate-fade-in">
            <CardContent className="py-6 text-center">
              <p className="text-sm text-slate-500 uppercase tracking-wider mb-2">Time Worked Today</p>
              <p className="text-4xl font-mono font-bold text-[hsl(243,65%,20%)]" data-testid="work-duration">
                {getWorkDuration()}
              </p>
              <p className="text-sm text-slate-400 mt-2">
                Clocked in at {new Date(timeEntry.clock_in).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                {timeEntry.office_name && ` • ${timeEntry.office_name}`}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Break Buttons */}
        {timeEntry && (
          <Card className={`border-0 shadow-lg animate-fade-in ${breakDuration >= breakSettings.break_warning_threshold_minutes ? 'break-warning' : ''}`}>
            <CardHeader className="pb-2">
              <CardTitle className="text-lg font-['Manrope'] flex items-center gap-2">
                <Coffee className="w-5 h-5" />
                Break Time
              </CardTitle>
            </CardHeader>
            <CardContent>
              {currentBreak ? (
                <div className="space-y-4">
                  <div className="text-center py-4 bg-slate-50 rounded-xl">
                    <p className="text-sm text-slate-500 uppercase tracking-wider mb-1">
                      {currentBreak.break_type === 'break' ? 'General Break' : `${currentBreak.break_type.charAt(0).toUpperCase() + currentBreak.break_type.slice(1)} Break`}
                    </p>
                    <p className={`text-3xl font-mono font-bold ${breakDuration >= breakSettings.break_warning_threshold_minutes ? 'text-[hsl(var(--warning))]' : 'text-[hsl(243,65%,20%)]'}`} data-testid="break-duration">
                      {formatDuration(breakDuration)}
                    </p>
                    <p className="text-xs text-slate-400 mt-1">
                      Standard: {currentBreak.break_type === 'breakfast' 
                        ? breakSettings.breakfast_duration_minutes 
                        : currentBreak.break_type === 'lunch'
                        ? breakSettings.lunch_duration_minutes
                        : breakSettings.general_break_duration_minutes} min
                    </p>
                    {breakDuration >= breakSettings.break_warning_threshold_minutes && (
                      <p className="text-sm text-[hsl(var(--warning))] mt-2 font-medium">
                        Break exceeded {breakSettings.break_warning_threshold_minutes} min!
                      </p>
                    )}
                  </div>
                  <Button 
                    className="w-full h-12 bg-[hsl(243,65%,20%)] hover:bg-[hsl(243,65%,25%)]"
                    onClick={handleEndBreak}
                    disabled={isLoading}
                    data-testid="end-break-btn"
                  >
                    End Break
                  </Button>
                </div>
              ) : (
                <div className="flex gap-3">
                  <button
                    className="break-btn breakfast flex flex-col items-center justify-center gap-1"
                    onClick={() => handleStartBreak('breakfast')}
                    disabled={isLoading}
                    data-testid="breakfast-break-btn"
                  >
                    <div className="flex items-center gap-2">
                      <Coffee className="w-5 h-5" />
                      Breakfast
                    </div>
                    <span className="text-xs opacity-70">{breakSettings.breakfast_duration_minutes} min</span>
                  </button>
                  <button
                    className="break-btn lunch flex flex-col items-center justify-center gap-1"
                    onClick={() => handleStartBreak('lunch')}
                    disabled={isLoading}
                    data-testid="lunch-break-btn"
                  >
                    <div className="flex items-center gap-2">
                      <UtensilsCrossed className="w-5 h-5" />
                      Lunch
                    </div>
                    <span className="text-xs opacity-70">{breakSettings.lunch_duration_minutes} min</span>
                  </button>
                  <button
                    className="break-btn general flex flex-col items-center justify-center gap-1"
                    onClick={() => handleStartBreak('break')}
                    disabled={isLoading}
                    data-testid="general-break-btn"
                  >
                    <div className="flex items-center gap-2">
                      <Timer className="w-5 h-5" />
                      Break
                    </div>
                    <span className="text-xs opacity-70">{breakSettings.general_break_duration_minutes} min</span>
                  </button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Overtime Progress Card */}
        {/* Overtime Progress Card - Only show if admin allows */}
        {showPeriodProgress && (
          <div className="animate-fade-in" style={{ animationDelay: '0.4s' }}>
            <OvertimeProgressCard />
          </div>
        )}
      </main>

      {/* Notification Panel */}
      <NotificationPanel 
        open={showNotifications} 
        onClose={() => setShowNotifications(false)}
        onRead={() => setUnreadCount(prev => Math.max(0, prev - 1))}
      />

      {/* Face Enrollment Dialog */}
      <FaceEnrollment
        open={showEnrollmentDialog}
        onOpenChange={setShowEnrollmentDialog}
        onEnrollmentComplete={() => {
          setFaceEnrolled(true);
          setShowEnrollmentDialog(false);
        }}
      />

      {/* Face Verification Dialog */}
      <FaceVerification
        open={showVerificationDialog}
        onOpenChange={(open) => {
          setShowVerificationDialog(open);
          if (!open) setPendingClockIn(false);
        }}
        onVerificationComplete={onFaceVerificationComplete}
        livenessCheckEnabled={facialSettings?.liveness_check_enabled || false}
      />
    </div>
  );
};

export default EmployeeDashboard;
